'use client';

import React, { useEffect, useState } from "react";
import { Building2, History, Users, Clock, Sparkles, TrendingUp, TrendingDown, MoveRight } from "lucide-react";
import { useJigyasa, formatINR, findPastEnquiries, buildSmartSuggestions, buildCapacitySnapshot } from "./state";
import { Card } from "./parts";

// ============================================================
// Button 1 + 2 — Capacity-fit panel (shown on enquiry detail when seat_count > 0)
// ============================================================
export const CapacityFitPanel = ({ enquiry }) => {
  const { hostData, costume } = useJigyasa();
  const [fit, setFit] = useState(null);
  const [upcoming, setUpcoming] = useState(null);

  useEffect(() => {
    if (!costume.buttonsOn.liveStock) return;
    let alive = true;
    (async () => {
      const stock = await hostData.liveStock();
      const { computeCapacityFit } = await import("./hostData");
      const f = computeCapacityFit(stock, enquiry.seat_count);
      const u = await hostData.upcomingStock({ windowDays: 60 });
      if (alive) {
        setFit(f);
        setUpcoming(u);
      }
    })();
    return () => { alive = false; };
  }, [enquiry.id, enquiry.seat_count, hostData, costume]);

  if (!costume.buttonsOn.liveStock || !enquiry.seat_count || !fit) return null;

  const v = costume.vocab;
  const c = costume.copy;

  return (
    <Card data-testid="capacity-fit-panel" className="border-zinc-300 bg-zinc-50">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-700">
        <Building2 size={14} /> {c.capacityPanelTitle}
        <span className="ml-auto text-[10px] font-normal normal-case text-zinc-500">asking {enquiry.seat_count} {v.units}</span>
      </div>

      {fit.fits ? (
        <p className="text-sm text-zinc-800">
          <strong>{fit.fitLocation.locationName}</strong> can take all {enquiry.seat_count} {v.units} —
          {" "}{fit.fitLocation.free} {v.units} free right now.
        </p>
      ) : (
        <>
          <p className="text-sm font-medium text-amber-900">{c.capacityPanelNoFit}</p>
          <p className="mt-1 text-sm text-zinc-800">
            <strong>{c.capacityPanelBestFit}:</strong> {fit.bestPartial.locationName} — {fit.bestPartial.free} {v.units} ready. {fit.remaining} short.
          </p>
          {fit.phasedSecond && (
            <p className="mt-1 text-sm text-zinc-700">
              <strong>{c.capacityPanelPhased}:</strong> {fit.bestPartial.free} now at {fit.bestPartial.locationName} + {fit.remaining} at {fit.phasedSecond.locationName}.
            </p>
          )}
        </>
      )}

      <div className="mt-3 grid grid-cols-2 gap-1 text-xs sm:grid-cols-5">
        {fit.allLocations.map((l) => (
          <div key={l.name} className="rounded-md bg-white px-2 py-1 text-zinc-700 border border-zinc-200">
            <div className="truncate text-[11px] font-medium">{l.name}</div>
            <div className="text-zinc-500">{l.free} free</div>
          </div>
        ))}
      </div>

      {upcoming?.events?.length > 0 && (
        <div className="mt-3 border-t border-zinc-200 pt-2">
          <div className="mb-1 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-zinc-600">
            <Clock size={12} /> {c.capacityPanelUpcoming}
          </div>
          <ul className="space-y-0.5 text-xs text-zinc-700">
            {upcoming.events.slice(0, 3).map((e, i) => (
              <li key={i}>
                <span className="font-medium">{e.locationName}</span> {e.subLocation ? `· ${e.subLocation}` : ""} — {e.unitCount} {v.units} on {e.effectiveDate}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
};

// ============================================================
// Button 3 — Customer footprint banner (existing-customer / expansion play)
// ============================================================
export const FootprintBanner = ({ enquiry }) => {
  const { hostData, costume } = useJigyasa();
  const [match, setMatch] = useState(null);

  useEffect(() => {
    if (!costume.buttonsOn.customerFootprint) return;
    let alive = true;
    (async () => {
      const res = await hostData.customerFootprint({
        phone: enquiry.phone,
        company: enquiry.company,
      });
      if (alive) setMatch(res);
    })();
    return () => { alive = false; };
  }, [enquiry.id, enquiry.phone, enquiry.company, hostData, costume]);

  if (!match || !match.matched) return null;

  const c = costume.copy;
  const cust = match.customer;

  return (
    <div data-testid="footprint-banner" className="rounded-lg border border-emerald-300 bg-emerald-50 p-3">
      <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-800">
        <Users size={14} /> {c.footprintBannerLabel}
        <span className="rounded-full bg-emerald-200 px-2 py-0.5 text-[10px] font-medium text-emerald-900">
          matched on {match.matchedOn}
        </span>
      </div>
      <p className="text-sm text-emerald-900">
        <strong>{cust.customerName}</strong> is already with us since {cust.since}. Currently: {cust.plan}.
        Monthly value <strong>{formatINR(cust.monthlyValue)}</strong>. Health {cust.healthScore}/10.
      </p>
      {cust.units?.length > 1 && (
        <ul className="mt-1 text-xs text-emerald-800">
          {cust.units.map((u, i) => (
            <li key={i}>· {u.unitCount} seats at {u.locationName} ({u.subLocation}) — lease ends {u.leaseEnd}</li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-xs text-emerald-700">
        This isn&apos;t a new lead — it&apos;s an expansion conversation. Open with their existing footprint.
      </p>
    </div>
  );
};

// ============================================================
// Button 4 — Past enquiry banner (Jigyasa-side, not host-side)
// ============================================================
export const PastEnquiryBanner = ({ enquiry }) => {
  const { enquiries, costume, stages } = useJigyasa();
  const past = findPastEnquiries(enquiries, enquiry.id, enquiry.phone);
  if (past.length === 0) return null;

  const c = costume.copy;
  const last = past[0];
  const rawDays = Math.floor((Date.now() - new Date(last.created_at).getTime()) / (1000 * 60 * 60 * 24));
  const daysSince = Number.isFinite(rawDays) && rawDays >= 0 ? rawDays : null;
  const whenText = daysSince == null ? "earlier" : daysSince === 0 ? "today" : `${daysSince} day${daysSince === 1 ? "" : "s"} ago`;
  const stageLabel = stages.find((s) => s.key === last.stage)?.label || last.stage;

  return (
    <div data-testid="past-enquiry-banner" className="rounded-lg border border-amber-300 bg-amber-50 p-3">
      <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-amber-800">
        <History size={14} /> {c.pastEnquiryBannerLabel}
      </div>
      <p className="text-sm text-amber-900">
        This phone enquired <strong>{whenText}</strong> — ended as <strong>{stageLabel}</strong>
        {last.lost_reason ? ` (${last.lost_reason})` : ""}.
      </p>
      {last.notes && <p className="mt-1 text-xs text-amber-800">&ldquo;{last.notes}&rdquo;</p>}
      <p className="mt-2 text-xs text-amber-700">{c.pastEnquiryBannerNote}</p>
    </div>
  );
};

// ============================================================
// Button 5 — Pricing context (shown when at "sent_price" stage)
// ============================================================
export const PricingContextPanel = ({ enquiry }) => {
  const { hostData, costume } = useJigyasa();
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    if (!costume.buttonsOn.pricingAndOffers) return;
    let alive = true;
    (async () => {
      const p = await hostData.pricingAndOffers({ enquiryType: enquiry.enquiry_type });
      if (alive) setPricing(p);
    })();
    return () => { alive = false; };
  }, [enquiry.id, enquiry.enquiry_type, hostData, costume]);

  if (!pricing) return null;
  const c = costume.copy;
  const v = costume.vocab;
  const suggested = pricing.standardRate * (enquiry.seat_count || 1);

  return (
    <Card data-testid="pricing-context-panel" className="border-rose-200 bg-rose-50">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-rose-800">
        <Sparkles size={14} /> {c.pricingPanelTitle}
      </div>
      <p className="text-sm text-rose-900">
        <strong>{c.pricingPanelStandard}:</strong> {formatINR(pricing.standardRate)} per {v.unit} per month
        {enquiry.seat_count ? ` → ${formatINR(suggested)} for ${enquiry.seat_count} ${v.units}` : ""}.
      </p>
      {pricing.activeOffers?.length > 0 && (
        <div className="mt-2">
          <div className="text-xs font-medium uppercase tracking-wider text-rose-700">{c.pricingPanelOffersActive}</div>
          <ul className="text-xs text-rose-900">
            {pricing.activeOffers.map((o) => (
              <li key={o.offerCode}>· <strong>{o.offerCode}</strong> — {o.label} (valid till {o.validTill})</li>
            ))}
          </ul>
        </div>
      )}
      {pricing.recentCloses?.length > 0 && (
        <div className="mt-2">
          <div className="text-xs font-medium uppercase tracking-wider text-rose-700">{c.pricingPanelRecentCloses}</div>
          <ul className="text-xs text-rose-900">
            {pricing.recentCloses.slice(0, 3).map((r, i) => (
              <li key={i}>· {r.seatCount} {v.units} closed at {formatINR(r.monthlyValue)} ({r.locationName})</li>
            ))}
          </ul>
        </div>
      )}
      <p className="mt-2 text-xs text-rose-700">
        {c.pricingPanelDiscountLimit}: up to {pricing.operatorDiscountLimitPct}% off without approval.
      </p>
    </Card>
  );
};

// ============================================================
// Smart suggestions (button 7 derivatives + lapsed-lead) — operator home
// ============================================================
export const SmartSuggestionsSection = ({ onOpenEnquiry }) => {
  const { enquiries, hostData, costume } = useJigyasa();
  const [renewalPipeline, setRenewalPipeline] = useState(null);
  const [upcomingStock, setUpcomingStock] = useState(null);

  useEffect(() => {
    if (!costume.buttonsOn.renewalPipeline) return;
    let alive = true;
    (async () => {
      const [r, u] = await Promise.all([
        hostData.renewalPipeline({ windowDays: 60 }),
        hostData.upcomingStock({ windowDays: 60 }),
      ]);
      if (alive) {
        setRenewalPipeline(r);
        setUpcomingStock(u);
      }
    })();
    return () => { alive = false; };
  }, [hostData, costume]);

  if (!renewalPipeline || !upcomingStock) return null;
  const tasks = buildSmartSuggestions({ enquiries, renewalPipeline, upcomingStock });
  if (tasks.length === 0) return null;

  const c = costume.copy;

  return (
    <section className="mb-6" data-testid="smart-suggestions-section">
      <h2 className="mb-3 flex items-center gap-2 text-base font-semibold tracking-tight text-zinc-900 sm:text-lg">
        <Sparkles size={16} className="text-violet-700" /> {c.sectionSmartSuggestions}
      </h2>
      <div className="space-y-2">
        {tasks.map((t, i) => (
          <div
            key={i}
            data-testid={`smart-task-${t.kind}-${i}`}
            className="rounded-xl border border-violet-200 bg-violet-50 p-4 shadow-sm"
          >
            <div className="mb-1 flex items-center gap-2">
              <span className="rounded-full bg-violet-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-900">
                {t.kind === "expansion" ? c.smartSuggestionExpansion : c.smartSuggestionLapsedLead}
              </span>
            </div>
            <div className="text-sm font-medium text-zinc-900">{t.title}</div>
            <p className="mt-1 text-sm text-zinc-700">{t.body}</p>
            <button
              onClick={() => t.meta?.enquiryId && onOpenEnquiry?.(t.meta.enquiryId)}
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-violet-800 hover:underline"
            >
              {t.actionLabel} <MoveRight size={12} />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

// ============================================================
// Owner — capacity snapshot strip (button 1)
// ============================================================
export const OwnerCapacityStrip = () => {
  const { hostData, costume } = useJigyasa();
  const [stock, setStock] = useState(null);

  useEffect(() => {
    if (!costume.buttonsOn.liveStock) return;
    let alive = true;
    (async () => {
      const s = await hostData.liveStock();
      if (alive) setStock(s);
    })();
    return () => { alive = false; };
  }, [hostData, costume]);

  if (!stock) return null;
  const snap = buildCapacitySnapshot(stock);
  const c = costume.copy;
  const v = costume.vocab;

  return (
    <section className="mb-6" data-testid="owner-capacity-snapshot">
      <h2 className="mb-3 flex items-center gap-2 text-base font-semibold tracking-tight text-zinc-900 sm:text-lg">
        <Building2 size={16} /> {c.sectionCapacitySnapshot}
      </h2>
      <Card>
        <div className="mb-2 flex items-baseline justify-between">
          <div>
            <div className="text-3xl font-semibold text-zinc-900">{snap.occupancyPct}%</div>
            <div className="text-xs uppercase tracking-wider text-zinc-500">full · {snap.totalFree} {v.units} free</div>
          </div>
          <div className="text-right text-xs text-zinc-500">
            {snap.totalOccupied} / {snap.totalCapacity} {v.units}
          </div>
        </div>
        <div className="mb-3 h-2 w-full overflow-hidden rounded-full bg-zinc-200">
          <div className="h-full rounded-full bg-zinc-900" style={{ width: `${snap.occupancyPct}%` }} />
        </div>
        <div className="space-y-2">
          {snap.locations.map((l) => (
            <div key={l.name} className="text-xs">
              <div className="mb-0.5 flex justify-between">
                <span className="font-medium text-zinc-800">{l.name}</span>
                <span className="text-zinc-600">{l.pct}% · {l.free} free</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
                <div
                  className={`h-full rounded-full ${l.pct >= 95 ? "bg-red-600" : l.pct >= 80 ? "bg-amber-500" : "bg-emerald-600"}`}
                  style={{ width: `${l.pct}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </Card>
    </section>
  );
};

// ============================================================
// Owner — renewal radar (button 7)
// ============================================================
export const OwnerRenewalRadar = () => {
  const { hostData, costume } = useJigyasa();
  const [pipeline, setPipeline] = useState(null);

  useEffect(() => {
    if (!costume.buttonsOn.renewalPipeline) return;
    let alive = true;
    (async () => {
      const r = await hostData.renewalPipeline({ windowDays: 60 });
      if (alive) setPipeline(r);
    })();
    return () => { alive = false; };
  }, [hostData, costume]);

  if (!pipeline || pipeline.renewals.length === 0) return null;
  const c = costume.copy;

  return (
    <section className="mb-6" data-testid="owner-renewal-radar">
      <h2 className="mb-3 text-base font-semibold tracking-tight text-zinc-900 sm:text-lg">
        {c.sectionRenewalRadar}
      </h2>
      <div className="space-y-2">
        {pipeline.renewals.map((r) => {
          const Icon = r.growthSignal?.score > 0 ? TrendingUp : r.growthSignal?.score < 0 ? TrendingDown : null;
          const color = r.growthSignal?.score > 0
            ? "border-emerald-300 bg-emerald-50"
            : r.growthSignal?.score < 0
            ? "border-red-300 bg-red-50"
            : "border-zinc-200 bg-white";
          return (
            <div key={r.hostCustomerId} className={`rounded-xl border p-3 ${color}`}>
              <div className="flex items-center justify-between">
                <div className="font-medium text-zinc-900">{r.customerName}</div>
                <div className="text-xs text-zinc-600">{c.renewalRadarLeaseEnds} {r.daysUntilEnd}d</div>
              </div>
              <div className="text-xs text-zinc-600">{r.currentPlan} · {formatINR(r.monthlyValue)}/mo</div>
              {r.growthSignal && (
                <div className="mt-1.5 flex items-start gap-1 text-xs text-zinc-700">
                  {Icon && <Icon size={12} className="mt-0.5 shrink-0" />}
                  <span>{r.growthSignal.narrative}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};
